import React from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CallMissedRoundedIcon from '@mui/icons-material/CallMissedRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import RingVolumeRoundedIcon from '@mui/icons-material/RingVolumeRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import SentimentNeutralRoundedIcon from '@mui/icons-material/SentimentNeutralRounded';

import {
  CallScribeCallStatus,
  CallScribeConsentResult,
  CallScribeSentiment,
  CallScribeTimeEntryStatus,
  ICallScribeCallListItem,
} from 'services/callScribe/types';
import { statusLabel } from 'components/CallScribe/callLogColumns';
import { safeParseJsonArray } from 'components/CallScribe/reviewHelpers';
import Chip, {
  SentimentChip,
  StatusChip,
  TChipTone,
  TimeEntryChip,
  TopicChip,
} from 'components/CallScribe/ui/Chip';

const STATUS_LOOK: Record<CallScribeCallStatus, { tone: TChipTone; icon: React.ReactElement }> = {
  [CallScribeCallStatus.Initiated]: { tone: 'outline', icon: <AccessTimeRoundedIcon /> },
  [CallScribeCallStatus.Ringing]: { tone: 'outline', icon: <RingVolumeRoundedIcon /> },
  [CallScribeCallStatus.Answered]: { tone: 'info', icon: <PhoneInTalkRoundedIcon /> },
  [CallScribeCallStatus.Completed]: { tone: 'outline', icon: <CheckRoundedIcon /> },
  [CallScribeCallStatus.Failed]: { tone: 'negative', icon: <ErrorOutlineRoundedIcon /> },
  [CallScribeCallStatus.NoAnswer]: { tone: 'warning', icon: <CallMissedRoundedIcon /> },
  [CallScribeCallStatus.Busy]: { tone: 'warning', icon: <CallMissedRoundedIcon /> },
  [CallScribeCallStatus.Cancelled]: { tone: 'outline', icon: <CloseRoundedIcon /> },
  [CallScribeCallStatus.Unknown]: { tone: 'outline', icon: <HelpOutlineRoundedIcon /> },
};

export const MAX_TOPIC_CHIPS = 2;

export const parseTopics = (raw: string | null | undefined): string[] => {
  const list = safeParseJsonArray<unknown>(raw);
  const source = list.length > 0 || !raw || raw.trim().startsWith('[')
    ? list
    : raw.split(',');
  return source
    .filter((t): t is string => typeof t === 'string')
    .map(t => t.trim())
    .filter(t => t.length > 0);
};

export const CallStatusChip: React.FC<{ status: CallScribeCallStatus }> = ({ status }) => {
  const look = STATUS_LOOK[status] ?? STATUS_LOOK[CallScribeCallStatus.Unknown];
  return <StatusChip label={statusLabel(status)} tone={look.tone} icon={look.icon} />;
};

export const sentimentChipFor = (sentiment: CallScribeSentiment): React.ReactElement | null => {
  switch (sentiment) {
    case CallScribeSentiment.Positive:
      return <SentimentChip sentiment="positive" />;
    case CallScribeSentiment.Negative:
      return <SentimentChip sentiment="negative" />;
    case CallScribeSentiment.Neutral:
      return <SentimentChip sentiment="neutral" />;
    case CallScribeSentiment.Mixed:
      return (
        <Chip tone="neutral" icon={<SentimentNeutralRoundedIcon />}>
          Mixed
        </Chip>
      );
    default:
      return null;
  }
};

export const timeEntryChipFor = (call: ICallScribeCallListItem): React.ReactElement | null => {
  switch (call.linkStatus) {
    case CallScribeTimeEntryStatus.Posted:
      return <TimeEntryChip state="posted" />;
    case CallScribeTimeEntryStatus.Failed:
      return (
        <Chip tone="negative" icon={<ScheduleRoundedIcon />} title={call.linkErrorMessage ?? undefined}>
          Time entry failed
        </Chip>
      );
    case CallScribeTimeEntryStatus.Claimed:
      return (
        <Chip tone="info" icon={<ScheduleRoundedIcon />}>
          Posting time entry
        </Chip>
      );
    case CallScribeTimeEntryStatus.Suggested:
      return call.suggestedDurationMinutes != null
        ? <TimeEntryChip state="suggested" minutes={call.suggestedDurationMinutes} />
        : null;
    default:
      return null;
  }
};

const notRecorded = (call: ICallScribeCallListItem): string | null => {
  if (call.consentResult === CallScribeConsentResult.Declined) {
    return 'The customer declined to be recorded, so this call has no recording or transcript.';
  }
  if (!call.isCustomerRecordingOptIn) {
    return 'This customer is opted out of recording, so this call has no recording or transcript.';
  }
  return null;
};

const useStyles = makeStyles((_theme: Theme) => ({
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}));

export interface ICallChipRowProps {
  call: ICallScribeCallListItem;
  maxTopics?: number;
  className?: string;
}

const CallChipRow: React.FC<ICallChipRowProps> = ({ call, maxTopics = MAX_TOPIC_CHIPS, className }) => {
  const classes = useStyles();
  const topics = parseTopics(call.topics);
  const shown = topics.slice(0, maxTopics);
  const hidden = topics.slice(maxTopics);
  const notRecordedReason = notRecorded(call);
  const sentiment = call.hasIntelligence ? sentimentChipFor(call.sentiment) : null;
  const entry = timeEntryChipFor(call);

  return (
    <div className={[classes.row, className].filter(Boolean).join(' ')}>
      <CallStatusChip status={call.status} />
      {notRecordedReason ? (
        <Chip tone="neutral" icon={<MicOffRoundedIcon />} title={notRecordedReason}>
          Not recorded
        </Chip>
      ) : null}
      {sentiment}
      {entry}
      {shown.map(t => <TopicChip key={t} label={t} />)}
      {hidden.length > 0 ? (
        <Chip tone="outline" title={hidden.join(', ')}>
          <span aria-hidden="true">{`+${hidden.length}`}</span>
          <span className={classes.srOnly}>
            {`${hidden.length} more ${hidden.length === 1 ? 'topic' : 'topics'}: ${hidden.join(', ')}`}
          </span>
        </Chip>
      ) : null}
    </div>
  );
};

export default CallChipRow;
